import React, { useEffect, useRef, useState } from 'react';
import { GripVertical, X } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Tooltip from '../ui/Tooltip';
import { SUBTASK_TITLE_MAX } from '../../extensions/bundled/task-manager/subtasks';
import useEscapeFirst from './useEscapeFirst';

/**
 * One item of the task window's checklist (TaskSubtasks.js): grip · tick ·
 * title · ✕. Behaves like Linear / Todoist checklist items:
 *  - click the title (or Enter / F2 on it) to rename in place; Enter or
 *    leaving the field saves, Escape puts the old title back, an emptied
 *    title deletes the item;
 *  - the grip drags (dnd-kit), Alt+↑ / Alt+↓ moves the focused item;
 *  - ✕ (shown on hover / focus) deletes — undoable from the board's history.
 */
export default function TaskSubtaskRow({ item, readOnly = false, onToggle, onRename, onDelete, onMoveBy }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.title);
  const inputRef = useRef(null);
  const cancelledRef = useRef(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `subtask:${item.id}`,
    disabled: readOnly || editing,
  });

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = () => {
    if (readOnly) return;
    cancelledRef.current = false;
    setDraft(item.title);
    setEditing(true);
  };
  const save = () => {
    if (cancelledRef.current) return;
    setEditing(false);
    if (draft !== item.title) onRename(item.id, draft);
  };
  const cancel = () => {
    cancelledRef.current = true;
    setDraft(item.title);
    setEditing(false);
  };
  // Escape cancels the rename instead of closing the whole task window.
  useEscapeFirst(editing, cancel);

  const onKeyDown = (e) => {
    if (!e.altKey || editing || readOnly) return;
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      onMoveBy(item.id, e.key === 'ArrowUp' ? -1 : 1, e.target);
    }
  };

  const classes = [
    'task-subtask',
    item.done && 'task-subtask--done',
    editing && 'task-subtask--editing',
    isDragging && 'task-subtask--dragging',
  ].filter(Boolean).join(' ');

  return (
    <li
      ref={setNodeRef}
      className={classes}
      data-subtask-id={item.id}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      onKeyDown={onKeyDown}
    >
      {!readOnly && (
        <span className="task-subtask-grip" data-part="grip" aria-label={`Reorder "${item.title}"`} {...attributes} {...listeners}>
          <GripVertical size={12} aria-hidden />
        </span>
      )}
      <input
        type="checkbox"
        className="checkbox-brand task-subtask-check"
        data-part="check"
        checked={item.done}
        disabled={readOnly}
        aria-label={item.done ? `Mark "${item.title}" as not done` : `Mark "${item.title}" as done`}
        onChange={(e) => onToggle(item.id, e.target.checked)}
      />
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          className="modal-input task-subtask-edit"
          aria-label="Subtask title"
          value={draft}
          maxLength={SUBTASK_TITLE_MAX}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              save();
            }
          }}
        />
      ) : (
        <span
          className="task-subtask-title"
          data-part="title"
          role={readOnly ? undefined : 'button'}
          tabIndex={readOnly ? undefined : 0}
          aria-label={readOnly ? undefined : `Rename "${item.title}"`}
          onClick={startEdit}
          onKeyDown={(e) => {
            if (!e.altKey && (e.key === 'Enter' || e.key === 'F2')) {
              e.preventDefault();
              startEdit();
            }
          }}
        >
          {item.title}
        </span>
      )}
      {!readOnly && !editing && (
        <Tooltip content="Delete subtask" side="top">
          <button type="button" className="task-subtask-delete" data-part="delete" aria-label={`Delete "${item.title}"`} onClick={() => onDelete(item.id)}>
            <X size={13} aria-hidden />
          </button>
        </Tooltip>
      )}
    </li>
  );
}
